import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import type { TooltipItem } from 'chart.js'
import { Chart, BarElement, LinearScale, CategoryScale, Tooltip, Legend } from 'chart.js'
import { Bar as BarChart } from 'react-chartjs-2'
// @ts-ignore
import sort, { ASC } from 'sort-array-objects'

import { useAppContext } from '@/App.context'
import { useText, useTranslation } from '@/components/i18n/hooks'
import useInvoices from '@/hooks/invoices'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { dateToMonthName } from '@/util/formatters/dates'

import { Translation } from '../i18n/Translation'

Chart.register(BarElement, LinearScale, CategoryScale, Tooltip, Legend)

const getColors = (whiteLabel: WhiteLabel | null) => {
  switch (whiteLabel) {
    case 'ice':
      return {
        colorPrivate: 'rgba(23, 23, 23, 1)',
        colorWork: 'rgba(255, 204, 39, 1)',
      }
    case 'bechtle':
      return {
        colorPrivate: 'rgba(23, 23, 23, 1)',
        colorWork: 'rgba(0, 141, 88, 1)',
      }
    case 'elkjop':
      return {
        colorPrivate: '#e5e5e5',
        colorWork: '#88d41b',
      }
    case 'tietoevry':
      return {
        colorPrivate: '#063a54',
        colorWork: '#00ADD3',
      }
    case 'telia':
      return {
        colorPrivate: '#990AE3',
        colorWork: '#e4e4e4',
      }
    case 'csi':
      return {
        colorPrivate: '#901c4d',
        colorWork: '#e4e4e4',
      }
    case 'threeStepIT':
      return {
        colorPrivate: '#002723',
        colorWork: '#D3E5DD',
      }
    case 'komplett':
      return {
        colorPrivate: '#F7B912',
        colorWork: '#F7A600',
      }
    default:
      return {
        colorPrivate: 'rgba(243, 133, 44, 1)',
        colorWork: 'rgba(255, 192, 1, 1)',
      }
  }
}

const createDataSet = (options: { backgroundColor?: string; label?: string }) => ({
  fill: false,
  backgroundColor: options?.backgroundColor || 'rgba(255, 95, 1, 1)',
  label: options?.label || undefined,
  pointBorderWidth: 5,
  data: [] as number[],
})

export const Graph: FC<{ offset: number }> = ({ offset }) => {
  const { whiteLabel } = useAppContext()
  const invoices = useInvoices()
  const { formatNumber } = useFormattedNumberWithCurrency()
  const translate = useTranslation()

  const workText = useText('Jobb')
  const privateText = useText('Privat')

  const colors = getColors(whiteLabel)

  const datasets = [
    createDataSet({
      backgroundColor: colors.colorPrivate,
      label: privateText,
    }), // salary deduction
    createDataSet({
      backgroundColor: colors.colorWork,
      label: workText,
    }), // total
  ]

  const data = {
    labels: [] as string[],
    datasets,
  }

  data.datasets[0].data = []
  data.datasets[1].data = []

  let currentInvoices = invoices ? [...invoices] : []
  currentInvoices = sort([...currentInvoices], ['date'], ASC)
  const numberOfInvoices = currentInvoices.length

  for (let i = numberOfInvoices - offset - 4; i < numberOfInvoices - offset; i++) {
    const invoice = currentInvoices[i]

    if (invoice) {
      data.datasets[0].data.push(invoice.salary_deduction_amount + invoice.salary_deduction_vat)
      data.datasets[1].data.push(
        invoice.amount +
          invoice.vat -
          invoice.salary_deduction_amount -
          invoice.salary_deduction_vat
      )
      const monthName = translate(dateToMonthName(invoice.date))
      data.labels.push(monthName)
    }
  }

  const options = {
    scales: {
      x: {
        stacked: true,
        ticks: {
          display: true,
        },
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (tooltipModel: TooltipItem<'bar'>) => {
            const datasetLabel = tooltipModel.datasetIndex === 0 ? privateText : workText
            return (
              datasetLabel +
              ': ' +
              formatNumber({ amount: (tooltipModel.raw as number) || 0, precision: 2 })
            )
          },
        },
      },
    },
  }

  return (
    <div id="development-graph-container" className="section">
      <div className="header">
        <Translation id="Kostnadsutvikling" />
      </div>

      <BarChart data={data} options={options} />
    </div>
  )
}

export default Graph
